// userProfile.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  FormFeedback,
  Tooltip,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchProfile, editUser, resetPassword } from "../../slices/thunks";
import { resetProfileFlag } from "../../slices/auth/profile/reducer";
import NameInput from "../../common/NameInput";
import EmailInput from "../../common/EmailInput";
import PasswordInput from "../../common/PasswordInput";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user, success, error, loading } = useSelector((state) => state.Profile);
  const [formValues, setFormValues] = useState(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  useEffect(() => {
    dispatch(fetchProfile());

    return () => {
      dispatch(resetProfileFlag());
    };
  }, [dispatch]);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      console.log("User data received from API:", user);

      // Map API fields (firstName, lastName) to form fields (firstname, lastname)
      setFormValues({
        firstname: user.firstName || user.firstname || "",
        lastname: user.lastName || user.lastname || "",
        email: user.email || "",
        password: "",
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formValues || {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .nullable()
        .notRequired()
        .test(
          "password-validation",
          "Password must be at least 8 characters with uppercase, lowercase, number, and special character",
          function (value) {
            // If password field is empty, it's valid (no password change)
            if (!value || value.trim() === "") {
              return true;
            }
            // Validate password only if provided
            if (value.length < 8) {
              return this.createError({ message: "Password must be at least 8 characters" });
            }
            if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(value)) {
              return this.createError({
                message: "Password must contain at least one uppercase, one lowercase, one number, and one special character"
              });
            }
            return true;
          }
        ),
    }),
    onSubmit: async (values) => {
      const hasPasswordChange = values.password && values.password.trim() !== "";

      try {
        // 1. Always update profile (name/email)
        const profilePayload = {
          firstName: values.firstname,
          lastName: values.lastname,
          email: values.email,
        };

        console.log("Updating profile with:", profilePayload);
        await dispatch(editUser(profilePayload));

        // 2. Only reset password if password field has value
        if (hasPasswordChange) {
          setIsResettingPassword(true);
          console.log("Resetting password...");

          const passwordPayload = {
            password: values.password,
            // Add any other required fields for password reset API
            // For example, you might need token or userId
            // token: user.token,
            // userId: user.id
          };

          await dispatch(resetPassword(passwordPayload));
          setIsResettingPassword(false);
        }

        // 3. Refresh profile data
        dispatch(fetchProfile());

        // 4. Clear password field after successful submission
        if (hasPasswordChange) {
          validation.setFieldValue("password", "");
        }

      } catch (error) {
        console.error("Error during update:", error);
        setIsResettingPassword(false);
      }
    },
  });

  const isLoading = loading || isResettingPassword;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary"></div>
        <span className="ms-2">Loading profile...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Edit Profile' pageTitle='Profile' />
        </Container>
      </div>
      <div className="table-container" style={{ marginTop: "15px" }}>
        <Card>
          <CardBody>
            <Form onSubmit={validation.handleSubmit}>
              <NameInput
                name="firstname"
                id="firstname"
                label="First Name"
                required={true}
                value={validation?.values?.firstname}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={validation?.touched?.firstname && validation?.errors?.firstname}
              />

              <NameInput
                name="lastname"
                id="lastname"
                label="Last Name"
                required={true}
                value={validation?.values?.lastname}
                onChange={validation.handleChange}
                onBlur={validation?.handleBlur}
                error={validation?.touched?.lastname && validation?.errors?.lastname}
              />

              <EmailInput
                name="email"
                id="email"
                required={true}
                layout="below"
                showHelp={false}
                disabled={true}
                value={validation?.values?.email}
              />

              {/* Password field - For password reset only */}
              <div className="mb-3">
                <Label htmlFor="password" className="form-label d-flex align-items-center">
                  <span>Password</span>
                  <span
                    id="passwordTooltip"
                    className="ms-1"
                    style={{ position: "relative", top: "-2px", cursor: "pointer" }}
                  >
                    <i className="ri-question-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                  </span>
                </Label>
                <Tooltip
                  placement="right"
                  isOpen={tooltipOpen}
                  target="passwordTooltip"
                  toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                  Leave blank to keep current password. Enter new password to change it.
                </Tooltip>
                <div className="position-relative">
                  <InputGroup>
                    <Input
                      type={passwordShow ? "text" : "password"}
                      name="password"
                      id="password"
                      className="form-control pe-5"
                      placeholder="Enter new password (leave blank to keep current)"
                      value={validation.values.password}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                    <InputGroupText
                      style={{
                        cursor: "pointer",
                        backgroundColor: "#f8f9fa"
                      }}
                      className="border-start-0"
                      onClick={() => setPasswordShow(!passwordShow)}
                    >
                      <i
                        className={passwordShow ? "ri-eye-off-fill align-middle" : "ri-eye-fill align-middle"}
                        style={{
                          color: "#6c757d"
                        }}
                      ></i>
                    </InputGroupText>
                  </InputGroup>
                  {validation.touched.password && validation.errors.password && (
                    <div className="text-danger mt-1">{validation.errors.password}</div>
                  )}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.875em" }}>
                  <i className="ri-information-line"></i> Password must contain uppercase, lowercase, number, and special character
                </div>
              </div>

              <div className="text-center mt-4">
                <Button
                  type="submit"
                  style={{ backgroundColor: "#0d9582", borderColor: "#0d9582" }}
                  disabled={isLoading || validation.isSubmitting}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {isResettingPassword ? "Updating Password..." : "Updating Profile..."}
                    </>
                  ) : (
                    "Update Profile"
                  )}
                </Button>

                {/* Show separate status messages */}
                {validation.values.password && validation.values.password.trim() !== "" && (
                  <div className="mt-2 text-info">
                    <small>
                      <i className="ri-information-line me-1"></i>
                      Both profile details and password will be updated
                    </small>
                  </div>
                )}
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;